import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ReCaptchaV3Service} from 'ng-recaptcha-2';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import {CountryService} from 'src/app/data-repositories/country-service';
import {LanguageService} from 'src/app/data-repositories/language.service';
import {PhoneCodeData} from 'src/app/data-repositories/phone-code-data';
import {CountryInfoModel} from 'src/app/models/country-info-model';
import {LanguageInfoModel} from 'src/app/models/language-info-model';
import {AppConfigService} from 'src/app/repositories/app-config.service';
import {AuthService} from 'src/app/repositories/auth.service';
import {BrowserStorageService} from 'src/app/services/browser-storage.service';
import {LoginDataService} from '../../data-repositories/login-data.service';
import {User} from '../../models/user';
import {StagesNavigationService} from '../workspace/stages-navigation/stages-navigation.service';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  // @Output() localizeItem: EventEmitter<any> = new EventEmitter();

  public loginModel = new User();
  public phoneCode: string;
  public phoneNumber: string;
  public isDisabled = true;
  public countriesInfo: Array<CountryInfoModel> = new Array<CountryInfoModel>();
  currentFlag: string;
  isBusy: boolean;
  currentLanguage: string;
  languagesList: LanguageInfoModel[];
  isAgree: boolean;
  private recaptchaV3ServiceSubscription: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private countryService: CountryService,
    private appConfigService: AppConfigService,
    private loginService: LoginDataService,
    public toastr: ToastrService,
    public phoneCodesData: PhoneCodeData,
    private browseStorage: BrowserStorageService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private stagesNavigationService: StagesNavigationService,
  ) {
    this.currentLanguage = browseStorage.getLocal('lang');

    this.loadCountryCodeAsync();

    loginService.getDefaultUser((user) => {
      this.loginModel = user;
    });
    countryService.getCountriesInfo(
      browseStorage.getLocal('lang'),
      (result) => {
        this.countriesInfo = result;
        if (
          this.countryService.getDefaultCountryCode() &&
          this.countriesInfo.some(
            (code) =>
              code.countryCode.toUpperCase() ===
              this.countryService.getDefaultCountryCode().toUpperCase()
          )
        ) {
          this.phoneCode = this.countriesInfo.find(
            (code) =>
              code.countryCode.toUpperCase() ===
              this.countryService.getDefaultCountryCode().toUpperCase()
          ).phoneCode;
        }
      }
    );

    this.languageService.getLanguagesInfoSubject().subscribe((langs) => {
      this.languagesList = langs;
    });
  }

  get videoTutorialSrc(): string {
    const tutorialKey = this.stagesNavigationService.isMobile
      ? 'VideoTutorialMobile.Login'
      : 'VideoTutorial.Login';

    const value = this.translateService.instant(tutorialKey);
    return (value === tutorialKey ? null : value);
  }

  ngOnDestroy(): void {
    this.recaptchaV3ServiceSubscription.unsubscribe();
  }

  ngOnInit() {
  }

  public validateUser() {
    this.isBusy = true;

    if (!this.phoneCode) {
      // {(!this.loginModel.email) {
      this.toastr.warning(this.translateService.instant('Login.Errors.EmptyCode'),
        this.translateService.instant('Login.Errors.Warning'));
      this.isBusy = false;
      return;
    }

    if (this.recaptchaV3ServiceSubscription) {
      this.recaptchaV3ServiceSubscription.unsubscribe();
    }

    this.recaptchaV3ServiceSubscription = this.recaptchaV3Service
      .execute('login')
      .subscribe(
        (token) => {
          this.loginModel.phone = this.phoneCode + this.phoneNumber;

          const initialUrl = this.browseStorage.getSession('initialUrl');

          this.loginService.loginUser(
            this.loginModel.phone,
            token,
            false,
            initialUrl,
            (user) => {
              this.loginModel = user;
              this.authService.waitForConfirmation(
                user.vid,
                user.phone
              );

              const queryParams = this.route.snapshot.queryParams;
              this.router.navigate(['confirmation'], {queryParams});
              this.isBusy = false;
            },
            (e) => {
              this.isBusy = false;
              this.toastr.error(
                `${this.translateService.instant(
                  'FailedLogin'
                )}. ${this.translateService.instant('ContactSupport')}`
              );
            }
          );
        },
        () =>
          this.toastr.error(
            `${this.translateService.instant(
              'FailedCaptcha'
            )}. ${this.translateService.instant('ContactSupport')}`
          )
      );
  }

  filter(countriesInfo: Array<CountryInfoModel>, filter: string) {
    if (filter && filter.trim().length > 0) {
      return countriesInfo.filter((x) =>
        x.countryTranslation.toLowerCase().startsWith(filter.toLowerCase())
      );
    }

    return countriesInfo;
  }

  refreshCurrentInfo(info: CountryInfoModel) {
    this.currentFlag = info ? info.countryCode : '';
  }

  public changeLang(lang: string) {
    this.browseStorage.setLocal('lang', lang.toLocaleLowerCase());
    this.translateService.use(lang.toLocaleLowerCase());
    this.currentLanguage = lang;
  }

  private async loadCountryCodeAsync(): Promise<any> {
    const result = await this.countryService.getCountryNameByIP();

    this.refreshCountries();
    return result;
  }

  private refreshCountries() {
    this.countryService.getCountriesInfo(
      this.browseStorage.getLocal('lang'),
      (result) => {
        this.countriesInfo = result;
        if (
          this.countryService.getDefaultCountryCode() &&
          this.countriesInfo.some(
            (code) =>
              code.countryCode.toUpperCase() ===
              this.countryService.getDefaultCountryCode().toUpperCase()
          )
        ) {
          const info = this.countriesInfo.find(
            (code) =>
              code.countryCode.toUpperCase() ===
              this.countryService.getDefaultCountryCode().toUpperCase()
          );
          this.phoneCode = info.phoneCode;
          this.refreshCurrentInfo(info);
        }
      }
    );
  }
}
